"use client";

import React from "react";
import { DISPLAY_WA_NUMBER, WA_NUMBER } from "./constants";
import KontakTopicGrid from "./components/KontakTopicGrid";
import KontakCustomForm from "./components/KontakCustomForm";
import KontakOfficeInfo from "./components/KontakOfficeInfo";
import KontakFaqAccordion from "./components/KontakFaqAccordion";

export default function KontakSection() {
  const defaultWaLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    "Halo Galura, saya ingin berkonsultasi mengenai solusi aplikasi & software.",
  )}`;

  return (
    <section className="relative overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 transition-colors duration-300 pt-28 pb-20 md:pt-36 md:pb-28">
      <div className="absolute inset-0 pointer-events-none -z-10 bg-[radial-gradient(circle_at_top_center,_rgba(34,197,94,0.12),_transparent_40%),radial-gradient(circle_at_bottom_left,_rgba(67,97,238,0.1),_transparent_35%)]" />

      <div className="container relative mx-auto px-4 sm:px-6 md:px-10 max-w-6xl">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-300/60 dark:border-emerald-800 shadow-sm mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            WhatsApp Live Fast Response (24/7)
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            Hubungi Tim Galura via{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-green-500 to-teal-500">
              WhatsApp Official
            </span>
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed px-2 sm:px-0">
            Konsultasi gratis, tanya jawab paket, hingga diskusi kebutuhan
            sistem perusahaan Anda dapat terhubung langsung ke WhatsApp hotline
            resmi kami tanpa ribet.
          </p>
        </div>

        {/* Quick Contact Highlight Bar */}
        <div className="mb-14 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 border border-emerald-800/40 text-white shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 text-emerald-400 flex items-center justify-center text-3xl shrink-0">
              💬
            </div>
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-400 block mb-0.5">
                Nomor Official WhatsApp Hotline
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                {DISPLAY_WA_NUMBER}
              </h3>
              <p className="text-xs text-slate-300 mt-1">
                Senin – Minggu • Fast Response (Rata-rata balasan &lt; 5 Menit)
              </p>
            </div>
          </div>

          <a
            href={defaultWaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full md:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-6 sm:px-8 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-105 transition-all duration-300 shrink-0"
          >
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-1.157 4.228 4.24-1.111z" />
            </svg>
            <span>Chat WhatsApp Langsung</span>
          </a>
        </div>

        {/* 4 Topic Cards */}
        <KontakTopicGrid />

        {/* Custom Form & Office Info */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <KontakCustomForm />
          <KontakOfficeInfo />
        </div>

        {/* Location Section - Google Maps Solutions Locator Plus */}
        <div id="location" className="mb-16 scroll-mt-28">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-8 dark:border-slate-800 dark:bg-slate-900 shadow-xl overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-300/60 dark:border-emerald-800 shadow-sm mb-2">
                  <span className="w-2 h-2 rounded-full bg-[#1f9913] animate-pulse" />
                  Google Maps Locator
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Lokasi Kantor &amp; Operational Studio Galura
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Temukan rute tercepat dan lokasi kantor Galura melalui Google Maps Locator interaktif di bawah ini.
                </p>
              </div>

              <a
                href="https://maps.google.com/?q=Galura+IT+Solution+Bandung"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold text-white bg-[#1f9913] hover:bg-[#187e0f] transition-all shadow-md shrink-0 self-start sm:self-auto"
              >
                <span>Buka di Google Maps</span>
                <span className="text-sm">↗</span>
              </a>
            </div>

            <div className="relative w-full h-[500px] sm:h-[600px] rounded-2xl overflow-hidden border border-slate-200/80 dark:border-slate-800 shadow-inner bg-slate-100 dark:bg-slate-950">
              <iframe
                src="https://storage.googleapis.com/maps-solutions-5oil6bwmfv/locator-plus/jjvg/locator-plus.html"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                title="Galura Google Maps Locator Plus"
              />
            </div>
          </div>
        </div>

        {/* FAQs */}
        <KontakFaqAccordion />
      </div>
    </section>
  );
}
